import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ViewCarouselIcon from "@mui/icons-material/ViewCarouselOutlined";
import ImageIcon from "@mui/icons-material/ImageOutlined";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  carouselIssues,
  emptyCarousel,
  type Carousel,
  type CarouselTitle,
} from "@shared/carousel";
import { AVAILABLE_LANGUAGES } from "../LanguageTabs";
import CataloguePickerDrawer from "./CataloguePickerDrawer";
import CarouselPreview from "./CarouselPreview";
import PickedTitlesList from "./PickedTitlesList";
import { useVodTitles } from "./useVodTitles";
import { carouselFindingText, saveErrorText } from "./findings";
import {
  foyerApi,
  type CarouselRow,
  type FeatureSetRow,
} from "../../http/foyer";
import OrderedSelection from "../OrderedSelection";
import { tokens } from "../../theme";
import { ItemFormSurface } from "../EditDrawer";
import { PermissionGate } from "../../auth/access";
import { AUTHOR_RAILS } from "./catalogueGates";

/**
 * Create / edit a carousel (BE-28). Figma `NendaPortal_1` node `707-12505`.
 *
 * ## Seed once, remount by key
 *
 * `useState(() => …)` and no prop-watching effect anywhere. The caller passes
 * `key={editing?.id ?? "new"}`, so opening a different record REMOUNTS this
 * component and the remount IS the reset. A "did the record change?" check
 * cannot tell *"opened a different one"* from *"reopened the same one"*, and
 * gets it wrong silently in both directions. See [[cms-list-and-editor]].
 *
 * ## What is deliberately absent
 *
 * - **A sort control.** `Carousel.sort` is dead on this stack — the Red Bee
 *   branch built a query string from it and the Content API branch ignores it —
 *   and BE-10 established the catalogue has no sorting at all. A control that
 *   does nothing is worse than no control. The stored value is carried through
 *   untouched so a copied row keeps it.
 * - **`recommended` and `continue_watching`.** Both are in upstream's
 *   `NON_TRANSFORMED_CAROUSEL_TYPES` and never reach a device on this stack. A
 *   copied row carrying one is editable and is reported; nobody can create a
 *   new one.
 * - **A "published" toggle.** There is no publish state on a carousel anywhere
 *   in this model. The only publish gate is `SubPage.draft`.
 */

export interface CarouselEditorDialogProps {
  open: boolean;
  /** The row being edited, or null to create. A row IS its draft. */
  initial: Carousel | null;
  saving?: boolean;
  /** The server's refusal, as it came back. */
  error?: { code?: string; message: string } | null;
  /** Called once the surface has finished sliding/fading away. */
  onClose: () => void;
  /** Persist the draft. Resolves true on success, which closes the surface. */
  onSubmit: (draft: Carousel) => Promise<boolean>;
}

/** Only the three the stack renders. See the docblock. */
const CREATABLE_TYPES = ["curated", "query", "epg"] as const;
type CreatableType = (typeof CREATABLE_TYPES)[number];
const isCreatable = (type: string): type is CreatableType =>
  (CREATABLE_TYPES as readonly string[]).includes(type);

const NAME_MAX = 100;

/**
 * `assetTypeFilter`, as an operator would say it.
 *
 * The field has always executed — `assetTypesOf()` sends it to the catalogue on
 * every query rail — and has never had a control, so the editor's own hint
 * ("filled automatically from tags, type and source") promised two things you
 * could not set. This is the `type` half.
 *
 * Deliberately NOT every combination: it is a free `[String]` upstream that can
 * hold `"*"` or anything else, and a control offering the power set would be
 * unreadable. A row outside this vocabulary keeps its value and says so.
 */
const KIND_VALUES: {
  value: "any" | "channels" | "movies" | "series" | "films-series";
  types: readonly string[];
}[] = [
  { value: "any", types: [] },
  { value: "channels", types: ["channel"] },
  { value: "movies", types: ["movie"] },
  { value: "series", types: ["series"] },
  { value: "films-series", types: ["movie", "series"] },
];

/** Set equality, because order in `assetTypeFilter` carries no meaning. */
function sameKind(
  kind: { types: readonly string[] },
  stored: string[]
): boolean {
  if (kind.types.length !== stored.length) return false;
  const have = new Set(stored);
  return kind.types.every((t) => have.has(t));
}

function Card({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        border: `1px solid ${tokens.divider}`,
        borderRadius: "14px",
        background: tokens.surface,
        p: 2.25,
        height: "100%",
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: "fontWeightBold" }}>
        {label}
      </Typography>
      {hint && (
        <Typography
          variant="meta"
          sx={{ color: tokens.inkFaint, mt: 0.25, mb: 1.5 }}
        >
          {hint}
        </Typography>
      )}
      {children}
    </Box>
  );
}

export default function CarouselEditorDialog({
  open,
  initial,
  saving,
  error,
  onClose,
  onSubmit,
}: CarouselEditorDialogProps) {
  const { t } = useTranslation(["content", "common"]);
  // Seeded once. Never synced from props — see the docblock.
  const [draft, setDraft] = useState<Carousel>(
    () => initial ?? emptyCarousel()
  );
  const [picker, setPicker] = useState(false);
  const [addingLanguage, setAddingLanguage] = useState(false);

  const set = <K extends keyof Carousel>(key: K, value: Carousel[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  // Derived over the draft in hand, by the same function the server and the
  // list row call. That is what stops a row and its editor wording the same
  // finding two different ways.
  const issues = useMemo(() => carouselIssues(draft), [draft]);

  /**
   * Resolved ONCE here, and handed to both the ordered list and the preview.
   * They read the same picks, so two independent lookups would double the reads
   * and could disagree with each other mid-flight. Only `curated` holds picks
   * at all — a query rail is filled per room and an epg rail fetches nothing.
   */
  const picks = useMemo(
    () => (draft.type === "curated" ? (draft.assetIds ?? []) : []),
    [draft.type, draft.assetIds]
  );
  const lookup = useVodTitles(picks);

  /**
   * The products a rail can point at, fetched only when one might.
   *
   * Lazy on purpose: a curated carousel — still the common case — never asks,
   * so opening the editor costs no request it does not need. Fetched once per
   * dialog rather than lifted to the page because the dialog is remounted per
   * open anyway, so "once per mount" and "once per open" are the same thing.
   */
  const [products, setProducts] = useState<FeatureSetRow[] | null>(null);
  /**
   * The catalogue, for picking the rows a page shows.
   *
   * Best-effort and read once: this dialog is already gated on
   * `canManageDistribution`, so a failure here is a network blip rather than a
   * permission, and the page body degrades to whatever it already holds.
   */
  const [pageOptions, setPageOptions] = useState<CarouselRow[] | null>(null);

  useEffect(() => {
    let alive = true;
    // 🚨 The CALL is inside the try, not just the promise. A `.catch()` alone
    // does not fire when the method is missing entirely — which is what a
    // partial test double looks like, and it takes the whole dialog down
    // rather than costing one picker. Same trap as `CarouselPreview`'s
    // line-up read; see the design-system note.
    void (async () => {
      try {
        const r = await foyerApi.listCarousels();
        if (alive) setPageOptions(r.carousels);
      } catch {
        if (alive) setPageOptions([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
  const wantsProducts = draft.type === "query";
  useEffect(() => {
    if (!wantsProducts || products !== null) return;
    let cancelled = false;
    void foyerApi
      .listFeatureSets()
      .then((res) => {
        if (!cancelled) setProducts(res.featureSets ?? []);
      })
      // An empty list is a real answer here: the select falls back to naming
      // the stored id, so a rail stays editable even when the catalogue read
      // fails. Blanking the whole dialog over it would be worse.
      .catch(() => {
        if (!cancelled) setProducts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [wantsProducts, products]);

  /**
   * CONTENT products only. A capability (Frontlayer, Care, Signage) grants no
   * packages at all, so a rail pointing at one would resolve to nothing for
   * every room forever — offering it would be offering a guaranteed blank.
   */
  const contentProducts = useMemo(
    () => (products ?? []).filter((p) => !p.capability),
    [products]
  );

  const titles = draft.title ?? [];
  const [previewLanguage, setPreviewLanguage] = useState<string | undefined>(
    () => titles[0]?.language
  );

  const usedLanguages = new Set(titles.map((t) => t.language?.toLowerCase()));
  const addableLanguages = AVAILABLE_LANGUAGES.filter(
    (l) => !usedLanguages.has(l)
  );

  const setTitle = (index: number, patch: Partial<CarouselTitle>) =>
    set(
      "title",
      titles.map((t, i) => (i === index ? { ...t, ...patch } : t))
    );

  const isCreate = initial === null;

  return (
    <>
      <ItemFormSurface
        mode={isCreate ? "create" : "edit"}
        open={open}
        onClose={onClose}
        title={
          isCreate
            ? t("carouselEditorDialog.createTitle")
            : t("carouselEditorDialog.editTitle")
        }
        subtitle={t("carouselEditorDialog.subtitle")}
        icon={<ViewCarouselIcon />}
        maxWidth="lg"
        drawerWidth={1040}
        busy={saving}
        actions={(dismiss) => (
          <>
            <Button onClick={() => dismiss()} disabled={saving}>
              {t("common:buttons.cancel")}
            </Button>
            <PermissionGate {...AUTHOR_RAILS} deniedProps={{ disabled: true }}>
              <Button
                variant="contained"
                disabled={saving || !draft.name?.trim()}
                onClick={() =>
                  // The page saves; on success the surface slides away first,
                  // and only then does the page unmount it (via onClose).
                  void onSubmit(draft).then((ok) => ok && dismiss())
                }
              >
                {saving ? t("common:states.saving") : t("common:buttons.save")}
              </Button>
            </PermissionGate>
          </>
        )}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {saveErrorText(t, error.code, error.message)}
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 2,
            mb: 2,
          }}
        >
          <Card
            label={t("carouselEditorDialog.name")}
            hint={t("carouselEditorDialog.nameHint")}
          >
            <TextField
              fullWidth
              size="small"
              placeholder={t("carouselEditorDialog.namePlaceholder")}
              value={draft.name ?? ""}
              onChange={(e) => set("name", e.target.value.slice(0, NAME_MAX))}
              helperText={t("carouselEditorDialog.nameLength", {
                length: (draft.name ?? "").length,
                max: NAME_MAX,
              })}
              inputProps={{ "aria-label": t("carouselEditorDialog.name") }}
            />
            {/* The id is derived from the name and NEVER moves afterwards:
                  re-minting it on rename would break every room type pointing
                  at it, silently, because nothing here has referential
                  integrity. So an existing row states its id rather than
                  implying the name still controls it. */}
            {!isCreate && (
              <Typography
                variant="meta"
                sx={{ color: tokens.inkFaint, mt: 0.5 }}
              >
                <Trans
                  t={t}
                  i18nKey="carouselEditorDialog.idNote"
                  values={{ id: draft.id }}
                  components={{ code: <code /> }}
                />
              </Typography>
            )}
          </Card>

          <Card
            label={t("carouselEditorDialog.titles")}
            hint={t("carouselEditorDialog.titlesHint")}
          >
            <Stack spacing={1}>
              {titles.map((title, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center">
                  <TextField
                    select
                    size="small"
                    value={title.language ?? ""}
                    onChange={(e) => setTitle(i, { language: e.target.value })}
                    sx={{ width: 96 }}
                    inputProps={{
                      "aria-label": t("carouselEditorDialog.language"),
                    }}
                  >
                    {AVAILABLE_LANGUAGES.map((l) => (
                      <MenuItem key={l} value={l}>
                        {l.toUpperCase()}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={t("carouselEditorDialog.titlePlaceholder")}
                    value={title.text ?? ""}
                    onChange={(e) => setTitle(i, { text: e.target.value })}
                    inputProps={{
                      "aria-label": t("carouselEditorDialog.titleFor", {
                        language: title.language ?? "",
                      }),
                    }}
                  />
                  <IconButton
                    size="small"
                    aria-label={t("carouselEditorDialog.removeTitle")}
                    onClick={() =>
                      set(
                        "title",
                        titles.filter((_, j) => j !== i)
                      )
                    }
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              {addingLanguage ? (
                <TextField
                  select
                  size="small"
                  label={t("carouselEditorDialog.addLanguage")}
                  value=""
                  onChange={(e) => {
                    set("title", [
                      ...titles,
                      { language: e.target.value, text: "" },
                    ]);
                    setAddingLanguage(false);
                  }}
                >
                  {addableLanguages.map((l) => (
                    <MenuItem key={l} value={l}>
                      {l.toUpperCase()}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  disabled={addableLanguages.length === 0}
                  onClick={() => setAddingLanguage(true)}
                  sx={{ alignSelf: "flex-start" }}
                >
                  {t("carouselEditorDialog.addLanguage")}
                </Button>
              )}
            </Stack>
          </Card>

          <Card
            label={t("carouselEditorDialog.displayLimit")}
            hint={t("carouselEditorDialog.displayLimitHint")}
          >
            <TextField
              type="number"
              size="small"
              fullWidth
              value={draft.displayLimit ?? ""}
              onChange={(e) =>
                // `Number("")` is 0, not NaN — an empty box must stay
                // undefined rather than storing a confident zero.
                set(
                  "displayLimit",
                  e.target.value === "" ? undefined : Number(e.target.value)
                )
              }
              inputProps={{
                min: 1,
                "aria-label": t("carouselEditorDialog.displayLimit"),
              }}
            />
          </Card>

          <Card
            label={t("carouselEditorDialog.showUnpurchased")}
            hint={t("carouselEditorDialog.showUnpurchasedHint")}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Switch
                checked={draft.showNonPurchasedContent ?? true}
                onChange={(e) =>
                  set("showNonPurchasedContent", e.target.checked)
                }
                inputProps={{
                  "aria-label": t("carouselEditorDialog.showUnpurchased"),
                }}
              />
              <Typography variant="bodySm">
                {t("carouselEditorDialog.showEverything")}
              </Typography>
            </Stack>
            {/* The flag on its own decides nothing, and an operator who does
                  not know that will report the switch as broken. */}
            <Typography variant="meta" sx={{ color: tokens.inkFaint, mt: 1 }}>
              {t("carouselEditorDialog.showUnpurchasedNote")}
            </Typography>
          </Card>

          {/* 🚨 A rail promoting itself into the MENU.
                Ruled by Sigge (2026-08-22): content presence is what should
                trigger a page. A rail with this on IS a tab — nobody composes
                one — so a hotel that buys the content gets the tab, the way
                buying Sports already produces the Sport rail. */}
          <Card
            label={t("carouselEditorDialog.showAsPage")}
            hint={t("carouselEditorDialog.showAsPageHint")}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Switch
                checked={draft.page?.enabled ?? false}
                onChange={(e) =>
                  set("page", {
                    enabled: e.target.checked,
                    // The body survives switching off, so switching back on
                    // restores the page that was arranged rather than an
                    // empty one.
                    label: draft.page?.label ?? [],
                    hideFromHome: draft.page?.hideFromHome ?? false,
                    carousels: draft.page?.carousels ?? [],
                  })
                }
                inputProps={{
                  "aria-label": t("carouselEditorDialog.showAsPage"),
                }}
                data-testid="carousel-page-enabled"
              />
              <Typography variant="bodySm">
                {t("carouselEditorDialog.nameInMenu")}
              </Typography>
            </Stack>

            {draft.page?.enabled && (
              <Stack spacing={2} sx={{ mt: 2 }}>
                {/* The body is NOT this rail. Sigge: "Movies as a rail on
                      main menu would be a curated list of top sellers. Movies
                      in the menu will open Action, Oldies but goldies, Marvel."
                      Said on screen because an empty page draws nothing and the
                      operator would otherwise report the toggle as broken. */}
                <OrderedSelection
                  label={t("carouselEditorDialog.rowsOnPage")}
                  hint={t("carouselEditorDialog.rowsOnPageHint")}
                  value={draft.page?.carousels ?? []}
                  options={(pageOptions ?? [])
                    .filter((c) => c.id !== draft.id)
                    .map((c) => ({ id: c.id, label: c.name || c.id }))}
                  onChange={(carousels) =>
                    set("page", {
                      enabled: true,
                      label: draft.page?.label ?? [],
                      hideFromHome: draft.page?.hideFromHome ?? false,
                      carousels,
                    })
                  }
                  testId="carousel-page-carousels"
                />

                {(draft.page?.carousels ?? []).length === 0 && (
                  <Alert severity="warning" data-testid="carousel-page-empty">
                    {t("carouselEditorDialog.pageEmpty")}
                  </Alert>
                )}

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Switch
                    checked={draft.page?.hideFromHome ?? false}
                    onChange={(e) =>
                      set("page", {
                        enabled: true,
                        label: draft.page?.label ?? [],
                        hideFromHome: e.target.checked,
                        carousels: draft.page?.carousels ?? [],
                      })
                    }
                    inputProps={{
                      "aria-label": t("carouselEditorDialog.hideFromHome"),
                    }}
                    data-testid="carousel-page-hide-from-home"
                  />
                  <Typography variant="bodySm">
                    {t("carouselEditorDialog.hideFromHomeLabel")}
                  </Typography>
                </Stack>
                <Typography variant="meta" sx={{ color: tokens.inkFaint }}>
                  {/* Default off, ruled by Sigge — a promoted rail keeps its
                        place unless somebody says otherwise. */}
                  {t("carouselEditorDialog.hideFromHomeNote")}
                </Typography>
              </Stack>
            )}
          </Card>

          <Card
            label={t("carouselEditorDialog.carouselType")}
            hint={t("carouselEditorDialog.carouselTypeHint")}
          >
            <TextField
              select
              size="small"
              fullWidth
              value={draft.type}
              onChange={(e) => set("type", e.target.value)}
              inputProps={{
                "aria-label": t("carouselEditorDialog.carouselType"),
              }}
            >
              {CREATABLE_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {t(`carouselEditorDialog.types.${type}.label`)}
                </MenuItem>
              ))}
              {/* A copied row can carry a type this stack does not render.
                    It stays selectable so the row remains editable — but it is
                    not offered to anybody creating one. */}
              {!isCreatable(draft.type) && (
                <MenuItem value={draft.type}>
                  {t("carouselEditorDialog.notRendered", { type: draft.type })}
                </MenuItem>
              )}
            </TextField>
            <Typography variant="meta" sx={{ color: tokens.inkFaint, mt: 1 }}>
              {isCreatable(draft.type)
                ? t(`carouselEditorDialog.types.${draft.type}.hint`)
                : ""}
            </Typography>
          </Card>

          {draft.type === "epg" ? (
            <Card
              label={t("carouselEditorDialog.channel")}
              hint={t("carouselEditorDialog.channelHint")}
            >
              <TextField
                size="small"
                fullWidth
                placeholder={t("carouselEditorDialog.channelPlaceholder")}
                value={draft.channelId ?? ""}
                onChange={(e) => set("channelId", e.target.value)}
                inputProps={{ "aria-label": t("carouselEditorDialog.channel") }}
              />
            </Card>
          ) : draft.type === "query" ? (
            <Card
              label={t("carouselEditorDialog.filledFrom")}
              hint={t("carouselEditorDialog.filledFromHint")}
            >
              <Stack spacing={2}>
                <Box>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label={t("carouselEditorDialog.product")}
                    value={draft.featureSetId ?? ""}
                    onChange={(e) =>
                      set("featureSetId", e.target.value || undefined)
                    }
                    inputProps={{
                      "aria-label": t("carouselEditorDialog.product"),
                    }}
                  >
                    <MenuItem value="">
                      <em>{t("carouselEditorDialog.noProduct")}</em>
                    </MenuItem>
                    {contentProducts.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                      </MenuItem>
                    ))}
                    {/* A stored id the catalogue did not return stays
                          selected and stays visible. Silently resetting it to
                          "no product" would rewrite the rail on the next save
                          because the operator opened a dialog. */}
                    {draft.featureSetId &&
                      !contentProducts.some(
                        (p) => p.id === draft.featureSetId
                      ) && (
                        <MenuItem value={draft.featureSetId}>
                          {t("carouselEditorDialog.notInCatalogue", {
                            id: draft.featureSetId,
                          })}
                        </MenuItem>
                      )}
                  </TextField>
                  <Typography
                    variant="meta"
                    sx={{ color: tokens.inkFaint, mt: 0.75 }}
                  >
                    {draft.featureSetId
                      ? t("carouselEditorDialog.productNote")
                      : t("carouselEditorDialog.noProductNote")}
                  </Typography>
                </Box>

                <TextField
                  select
                  size="small"
                  fullWidth
                  label={t("carouselEditorDialog.kind")}
                  value={
                    KIND_VALUES.find((k) =>
                      sameKind(k, draft.assetTypeFilter ?? [])
                    )?.value ?? "custom"
                  }
                  onChange={(e) => {
                    const chosen = KIND_VALUES.find(
                      (k) => k.value === e.target.value
                    );
                    if (chosen) set("assetTypeFilter", [...chosen.types]);
                  }}
                  inputProps={{ "aria-label": t("carouselEditorDialog.kind") }}
                >
                  {KIND_VALUES.map((k) => (
                    <MenuItem key={k.value} value={k.value}>
                      {t(`carouselEditorDialog.kinds.${k.value}`)}
                    </MenuItem>
                  ))}
                  {/* `assetTypeFilter` is a free [String] upstream and can
                        hold combinations this control cannot express — "*"
                        among them. A copied row keeps its value instead of
                        being flattened into the nearest offered option. */}
                  {!KIND_VALUES.some((k) =>
                    sameKind(k, draft.assetTypeFilter ?? [])
                  ) && (
                    <MenuItem value="custom">
                      {t("carouselEditorDialog.asStored", {
                        value: (draft.assetTypeFilter ?? []).join(", ") || "—",
                      })}
                    </MenuItem>
                  )}
                </TextField>

                <TextField
                  size="small"
                  fullWidth
                  label={t("carouselEditorDialog.tags")}
                  placeholder={t("carouselEditorDialog.tagsPlaceholder")}
                  value={(draft.tagIds ?? []).join(", ")}
                  onChange={(e) =>
                    set(
                      "tagIds",
                      e.target.value
                        .split(",")
                        .map((tag) => tag.trim())
                        .filter(Boolean)
                    )
                  }
                  inputProps={{ "aria-label": t("carouselEditorDialog.tags") }}
                />
              </Stack>
            </Card>
          ) : (
            <Card label={t("carouselEditorDialog.media")}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    background: tokens.rowAlt,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <ImageIcon sx={{ color: tokens.inkFaint }} />
                </Box>
                <Stack sx={{ flex: 1 }}>
                  <Typography
                    variant="bodySm"
                    sx={{ fontWeight: "fontWeightSemibold" }}
                  >
                    {picks.length === 0
                      ? t("carouselEditorDialog.noMediaPicked")
                      : t("carouselEditorDialog.picked", {
                          count: picks.length,
                        })}
                  </Typography>
                  <Typography variant="meta" sx={{ color: tokens.inkFaint }}>
                    {picks.length === 0
                      ? t("carouselEditorDialog.pickTitles")
                      : t("carouselEditorDialog.dragOrder")}
                  </Typography>
                </Stack>
                <Button variant="outlined" onClick={() => setPicker(true)}>
                  {t("carouselEditorDialog.pickMedia")}
                </Button>
              </Stack>

              {picks.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <PickedTitlesList
                    value={picks}
                    byId={lookup.byId}
                    unresolved={lookup.unresolved}
                    onChange={(next) => set("assetIds", next)}
                  />
                </Box>
              )}
            </Card>
          )}
        </Box>

        {issues.length > 0 && (
          <Stack spacing={0.5} sx={{ mb: 3 }}>
            {issues.map((issue, i) => (
              <Stack
                key={`${issue.code}-${issue.subject ?? i}`}
                direction="row"
                spacing={1}
                alignItems="flex-start"
              >
                <Chip
                  size="small"
                  label={t("carouselEditorDialog.note")}
                  sx={{ height: 20, typography: "micro" }}
                />
                <Typography variant="meta" sx={{ color: tokens.inkFaint }}>
                  {carouselFindingText(t, issue)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}

        <Stack
          direction="row"
          alignItems="baseline"
          spacing={2}
          sx={{ mb: 1.5 }}
        >
          <Typography variant="h6" sx={{ fontWeight: "fontWeightBold" }}>
            {t("carouselEditorDialog.preview")}
          </Typography>
          {titles.length > 1 && (
            <Stack direction="row" spacing={0.75}>
              {titles.map((title) => (
                <Chip
                  key={title.language}
                  size="small"
                  label={(title.language ?? "").toUpperCase()}
                  onClick={() => setPreviewLanguage(title.language)}
                  color={
                    previewLanguage === title.language ? "primary" : "default"
                  }
                  variant={
                    previewLanguage === title.language ? "filled" : "outlined"
                  }
                />
              ))}
            </Stack>
          )}
        </Stack>
        <CarouselPreview
          carousel={draft}
          language={previewLanguage}
          byId={lookup.byId}
          resolving={lookup.loading}
          // The rail IS the ordering surface now — arranging a row while
          // looking at the row needs no explaining. The list above keeps the
          // two jobs it cannot do: naming every pick, and removing one.
          onReorder={(ids) => set("assetIds", ids)}
          // A dynamic rail has no pick list, so its drag writes a SPARSE
          // overlay instead: the pins somebody moved, with everything else
          // keeping its place behind them.
          onPin={(ids) => set("assetOrder", ids)}
        />
      </ItemFormSurface>

      {picker && (
        <CataloguePickerDrawer
          // Remounted per open, so it seeds from the CURRENT picks rather than
          // whatever it held last time.
          key={(draft.assetIds ?? []).join(",")}
          open
          value={draft.assetIds ?? []}
          onClose={() => setPicker(false)}
          onConfirm={(ids) => {
            set("assetIds", ids);
            setPicker(false);
          }}
        />
      )}
    </>
  );
}
